import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, Clock, CheckCircle, Circle, FileText, Navigation, AlertCircle, Play, Square } from 'lucide-react';
import { toast } from 'sonner';
import { staffSelfAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ListPageHeader from '../../components/shared/ListPageHeader';

const VISIT_TYPE_LABELS = {
  ORDER: 'Order visit',
  COLLECTION: 'Collection',
  DELIVERY: 'Delivery',
  regular: 'Regular visit',
  REGULAR: 'Regular visit',
  FOLLOW_UP: 'Follow-up',
  SURVEY: 'Survey',
};

const MyVisitsPage = () => {
  const { businessId } = useParams();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchMyVisits();
  }, []);

  const fetchMyVisits = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await staffSelfAPI.getMyVisits();
      setVisits(Array.isArray(data) ? data : (data.visits || []));
    } catch (err) {
      console.error('Error fetching visits:', err);
      setError(getApiErrorMessage(err, 'Failed to load visits. Please try again.'));
      setVisits([]);
    } finally {
      setLoading(false);
    }
  };

  const getGpsCoords = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve({}),
        { timeout: 5000 }
      );
    });

  const handleCheckIn = async (visitId) => {
    try {
      setActionLoading({ ...actionLoading, [visitId]: true });
      const coords = await getGpsCoords();
      await staffSelfAPI.checkinVisit(visitId, coords);
      toast.success('Checked in successfully!');
      fetchMyVisits();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to check in'));
      console.error(error);
    } finally {
      setActionLoading({ ...actionLoading, [visitId]: false });
    }
  };

  const handleCheckOut = async (visitId) => {
    try {
      setActionLoading({ ...actionLoading, [visitId]: true });
      const coords = await getGpsCoords();
      await staffSelfAPI.checkoutVisit(visitId, coords);
      toast.success('Checked out successfully!');
      fetchMyVisits();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to check out'));
      console.error(error);
    } finally {
      setActionLoading({ ...actionLoading, [visitId]: false });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'bg-emerald-50 text-emerald-700';
      case 'IN_PROGRESS': return 'bg-blue-50 text-blue-700';
      case 'PLANNED': return 'bg-slate-100 text-slate-600';
      case 'MISSED': return 'bg-rose-50 text-rose-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED': return <CheckCircle className="w-3.5 h-3.5" />;
      case 'IN_PROGRESS': return <Circle className="w-3.5 h-3.5 fill-current" />;
      case 'MISSED': return <Circle className="w-3.5 h-3.5" />;
      default: return <Circle className="w-3.5 h-3.5" />;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return null;
    }
  };

  const stats = {
    total: visits.length,
    completed: visits.filter(v => v.status === 'COMPLETED').length,
    inProgress: visits.filter(v => v.status === 'IN_PROGRESS').length,
    pending: visits.filter(v => v.status === 'PLANNED').length,
  };

  return (
    <div className="h-full flex flex-col" data-testid="my-visits-page">
      <ListPageHeader
        title="My Visits & Tasks"
        subtitle="Manage your assigned visits for today"
        stats={[
          { icon: MapPin, label: 'Total', value: stats.total, color: 'slate' },
          { icon: CheckCircle, label: 'Completed', value: stats.completed, color: 'emerald' },
          { icon: Clock, label: 'In Progress', value: stats.inProgress, color: 'blue' },
          { icon: Circle, label: 'Pending', value: stats.pending, color: 'slate' },
        ]}
      />

      {/* Content Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading visits...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                <AlertCircle className="w-7 h-7 text-red-400" />
              </div>
              <p className="text-[14px] font-medium text-slate-700">Failed to load visits</p>
              <p className="text-[13px] text-slate-400 mt-1">{error}</p>
              <button
                onClick={fetchMyVisits}
                className="mt-4 px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Retry
              </button>
            </div>
          ) : visits.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No visits assigned for today</p>
              <p className="text-[13px] text-slate-400 mt-1">Check back later or contact your manager</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table table-fixed w-full">
                  <colgroup>
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '22%' }} />
                  </colgroup>
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className="tl-th tl-th-teal">Retailer</th>
                      <th className="tl-th tl-th-teal">Purpose</th>
                      <th className="tl-th tl-th-teal">Scheduled</th>
                      <th className="tl-th tl-th-teal">Check In</th>
                      <th className="tl-th tl-th-teal">Check Out</th>
                      <th className="tl-th tl-th-teal">Status</th>
                      <th className="tl-th tl-th-teal text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="animate-stagger">
                    {visits.map((visit, idx) => {
                      const isLoading = actionLoading[visit.id];
                      const canCheckIn = visit.status === 'PLANNED';
                      const canCheckOut = visit.status === 'IN_PROGRESS';
                      const isCompleted = visit.status === 'COMPLETED';

                      return (
                        <tr
                          key={visit.id}
                          className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                          style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                        >
                          <td className="tl-td">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-[12px] font-medium text-teal-700">
                                  {visit.party_name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <span className="tl-cell-primary truncate">{visit.party_name || 'Visit'}</span>
                            </div>
                          </td>
                          <td className="tl-td tl-cell-regular">
                            {VISIT_TYPE_LABELS[visit.visit_type] || visit.visit_type || 'Regular visit'}
                          </td>
                          <td className="tl-td tl-cell-regular">
                            {visit.scheduled_from_time || '---'}
                          </td>
                          <td className="tl-td tl-cell-regular">
                            {formatTime(visit.checkin_time) || <span className="tl-cell-empty">---</span>}
                          </td>
                          <td className="tl-td tl-cell-regular">
                            {formatTime(visit.checkout_time) || <span className="tl-cell-empty">---</span>}
                          </td>
                          <td className="tl-td">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${getStatusColor(visit.status)}`}>
                              {getStatusIcon(visit.status)}
                              {visit.status === 'IN_PROGRESS' ? 'Active' : visit.status === 'COMPLETED' ? 'Done' : visit.status}
                            </span>
                          </td>
                          <td className="tl-td text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {canCheckIn && (
                                <button
                                  onClick={() => handleCheckIn(visit.id)}
                                  disabled={isLoading}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors text-[11px] font-medium disabled:opacity-50"
                                >
                                  <Play className="w-3 h-3" />
                                  Check In
                                </button>
                              )}

                              {canCheckOut && (
                                <>
                                  <button
                                    onClick={() => handleCheckOut(visit.id)}
                                    disabled={isLoading}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-[11px] font-medium disabled:opacity-50"
                                  >
                                    <Square className="w-3 h-3" />
                                    Check Out
                                  </button>
                                  <Link to={`/business/${businessId}/sales/invoices/new`}>
                                    <button className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-white border border-slate-200 text-slate-700 rounded-md hover:bg-slate-50 transition-colors text-[11px] font-medium">
                                      <FileText className="w-3 h-3" />
                                      Invoice
                                    </button>
                                  </Link>
                                </>
                              )}

                              {isCompleted && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-50 text-emerald-700 rounded-md text-[11px] font-medium">
                                  <CheckCircle className="w-3 h-3" />
                                  Done
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{visits.length}</span> visits for today
                </p>
                <p>
                  Completed: <span className="font-medium text-emerald-600">{stats.completed}</span> •
                  Pending: <span className="font-medium text-slate-700">{stats.pending}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyVisitsPage;
